import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Drawer,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import { MoreVert } from '@mui/icons-material';

const sampleData = [
  { name: 'Jan', value: 400 },
  { name: 'Feb', value: 300 },
  { name: 'Mar', value: 500 },
  { name: 'Apr', value: 450 },
  { name: 'May', value: 600 },
  { name: 'Jun', value: 700 },
];

const Analytics = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleExport = () => {
    alert("Export functionality triggered");
  };

  const handleImport = () => {
    alert("Import functionality triggered");
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Main row: Sidebar + Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar for large screens */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={function (): void {
                      throw new Error("Function not implemented.");
                  } } />
        </Box>
        {/* Drawer for mobile screens */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu mobileOpen={false} onToggle={function (): void {
                      throw new Error("Function not implemented.");
                  } } />
        </Drawer>

        {/* Main content area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header with toggle button */}
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          {/* Content */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              padding: 2,
              paddingTop: '10px',
              backgroundColor: '#f5f5f5',
              position: 'relative',
            }}
          >
            {/* Top right icon */}
            <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
              <MoreVert />
            </Box>

            {/* Export & Import Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4, gap: 1 }}>
              <Button variant="contained" color="primary" onClick={handleExport}>
                Export
              </Button>
              <Button variant="outlined" color="primary" onClick={handleImport}>
                Import
              </Button>
            </Box>

            {/* Main report & analytics content */}
            <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
              {/* Header */}
              <Typography variant="h6" gutterBottom>
                Report & Analytics
              </Typography>

              {/* Stats cards */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                {/* Card 1 */}
                <Box
                  sx={{
                    flex: '1 1 200px',
                    backgroundColor: '#fff',
                    padding: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Total Customers
                  </Typography>
                  <Typography variant="h4" gutterBottom>
                    1630
                  </Typography>
                  <Typography variant="body2" color="green">
                    3.5% ↑
                  </Typography>
                </Box>
                {/* Card 2 */}
                <Box
                  sx={{
                    flex: '1 1 200px',
                    backgroundColor: '#fff',
                    padding: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Delivery Completed
                  </Typography>
                  <Typography variant="h4" gutterBottom>
                    1293
                  </Typography>
                  <Typography variant="body2" color="red">
                    2.5% ↓
                  </Typography>
                </Box>
                {/* Card 3 */}
                <Box
                  sx={{
                    flex: '1 1 200px',
                    backgroundColor: '#fff',
                    padding: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Revenue Generated
                  </Typography>
                  <Typography variant="h4" gutterBottom>
                    $75,000
                  </Typography>
                  <Typography variant="body2" color="green">
                    12% ↑
                  </Typography>
                </Box>
              </Box>

              {/* Charts Section */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {/* Spending Overview Chart */}
                <Box
                  sx={{
                    flex: '1 1 400px',
                    backgroundColor: '#fff',
                    padding: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    Spending Overview
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={sampleData}>
                      <CartesianGrid stroke="#e0e0e0" strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>

                {/* Stats Overview Chart */}
                <Box
                  sx={{
                    flex: '1 1 400px',
                    backgroundColor: '#fff',
                    padding: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    Stats Overview
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={sampleData}>
                      <CartesianGrid stroke="#e0e0e0" strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="value" stroke="#82ca9d" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Analytics;